import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Tabs,
  Tab,
  useTheme,
  Box,
  IconButton,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  School as SchoolIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import CustomButton from '../../components/ui/Button/CustomButton';
import SectionCard from '../../components/ui/SectionCard/SectionCard';

const tabRoutes = ['general', 'security', 'notifications', 'school', 'user'];
const tabLabels = [
  { label: 'General', icon: <SettingsIcon /> },
  { label: 'Security', icon: <SecurityIcon /> },
  { label: 'Notifications', icon: <NotificationsIcon /> },
  { label: 'School', icon: <SchoolIcon /> },
  { label: 'User', icon: <PersonIcon /> }
];

const SettingsNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [tabAnim, setTabAnim] = React.useState(false);

  const getCurrentTab = () => {
    const path = location.pathname;
    if (path.includes('/general')) return 0;
    if (path.includes('/security')) return 1;
    if (path.includes('/notifications')) return 2;
    if (path.includes('/school')) return 3;
    if (path.includes('/user')) return 4;
    return 0; // Default to general
  };

  const handleTabChange = (event, newValue) => {
    setTabAnim(true);
    setTimeout(() => setTabAnim(false), 350);
    navigate(`/settings/${tabRoutes[newValue]}`);
  };

  return (
    <SectionCard elevation={1} sx={{ borderRadius: 3, mb: { xs: 2, md: 3 }, px: { xs: 1, sm: 2 }, py: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          background: 'transparent',
          borderRadius: 0,
          overflow: 'visible',
          mb: 0,
          px: 0,
          pt: 0,
          pb: 0,
        }}
      >
      {/* Back to Dashboard */}
      <CustomButton
        variant="outlined"
        color="primary"
        size="small"
        startIcon={<ArrowBackIcon fontSize="medium" />}
        onClick={() => navigate('/dashboard')}
        sx={{
          mr: 1.5,
          minWidth: 0,
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          fontSize: 18,
        }}
      >
        Back
      </CustomButton>
      <Tabs
        value={getCurrentTab()}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 48,
          '& .MuiTabs-flexContainer': {
            gap: { xs: 0.5, sm: 1.5 },
          },
          '& .MuiTab-root': {
            minHeight: 44,
            minWidth: 120,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 17,
            borderRadius: 2.5,
            px: 2.5,
            py: 1.25,
            color: theme.palette.text.primary,
            background: 'none',
            position: 'relative',
            zIndex: 1,
            transition: 'background 0.18s, color 0.18s, box-shadow 0.18s',
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            '&.Mui-selected': {
              color: '#fff',
              background: 'linear-gradient(90deg, #ff6b35 0%, #ff9500 100%)',
              boxShadow: '0 2px 8px 0 rgba(255,107,53,0.13)',
              fontWeight: 800,
              borderRadius: 2.5,
              '&:after': {
                content: '""',
                position: 'absolute',
                left: 12,
                right: 12,
                bottom: -3,
                height: 4,
                borderRadius: 2,
                background: '#ff6b35',
                opacity: 1,
                zIndex: 2,
              },
            },
            '&:hover': {
              background: 'rgba(255,107,53,0.08)',
              color: theme.palette.primary.main,
            },
          },
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        }}
      >
        {tabLabels.map((tab, idx) => (
          <Tab
            key={tab.label}
            icon={tab.icon}
            label={tab.label}
            iconPosition="start"
            disableRipple
            sx={{
              ...(tabAnim && getCurrentTab() === idx
                ? {
                    animation: 'tabGlow 0.35s',
                    '@keyframes tabGlow': {
                      '0%': { boxShadow: '0 0 0 0 rgba(255,107,53,0.18)' },
                      '60%': { boxShadow: '0 0 16px 8px rgba(255,107,53,0.18)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(255,107,53,0.18)' },
                    },
                  }
                : {}),
            }}
          />
        ))}
      </Tabs>
      </Box>
    </SectionCard>
  );
};

export default SettingsNavigation; 